import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Search, ArrowLeft } from "lucide-react";
import { RestaurantCard, useAllRestaurantPlaces } from "./Restaurants";
import { TempleCard, TEMPLES } from "./Temples";
import { VenueCard, VENUES } from "./Venues";
import { DecoratorCard, DECORATORS } from "./Decorators";
import { MovieCard, MOVIES_DATA } from "./Movies";
import { GroceryCard, GROCERY_STORES } from "./Grocery";
import { ShoppingCard, SHOPPING_STORES } from "./Shopping";
import { EventCard, EVENTS, type EventItem } from "./Events";
import { CricketCard, CRICKET_ENTRIES } from "./Cricket";
import { HealthCard, HEALTH_PROVIDERS } from "./HealthWellness";
import { ArtistCard, PERFORMING_ARTISTS_DATA } from "./PerformingArtists";
import { NonProfitCard, NONPROFIT_ENTRIES } from "./NonProfitOrg";
import { PhotoVideoCard, PHOTO_VIDEO_ENTRIES } from "./PhotoVideo";
import { FinancialServiceCard, FINANCIAL_SERVICE_ENTRIES } from "./FinancialServices";
import { DessertCard, DESSERTS_DATA } from "./Desserts";
import { RealtorCard, REALTOR_ENTRIES } from "./Realtors";
import { OrganizerCard, EVENT_ORGANIZERS } from "./EventOrganizer";
import { SALON_SPA_STORES } from "./SalonSpa";
import { MakeupArtistCard, MAKEUP_ARTISTS } from "./MakeupHair";
import { DJCard, DJS } from "./DJs";
import { MediaCard, MEDIA_ITEMS } from "./Media";
import { KidsCard, KIDS_ENTRIES } from "./Kids";
import { CatererCard, CATERERS } from "./Catering";
import { SeniorCard, SENIOR_ENTRIES } from "./SeniorCitizen";
import { YouthCard, YOUTH_ENTRIES } from "./Youth";
import { LawyerCard, LAWYER_ENTRIES } from "./Lawyers";
import { HomeServiceCard, HOME_SERVICES_ENTRIES } from "./HomeServices";

// ── Category definitions with keywords for fuzzy matching ──
const ALL_CATEGORIES = [
  { name: "Restaurants", href: "/restaurants", keywords: ["restaurant", "restaurants", "food", "eat", "dining", "cuisine", "biryani", "dosa", "chaat", "buffet", "indian", "desi"] },
  { name: "Events", href: "/events", keywords: ["event", "events", "festival", "mela", "celebration"] },
  { name: "Concerts/Events", href: "/concerts", keywords: ["concert", "concerts", "music", "show", "live", "band", "singer"] },
  { name: "Temples/Spiritual Services", href: "/temples", keywords: ["temple", "temples", "mandir", "worship", "puja", "prayer", "spiritual", "spiritual services"] },
  { name: "Movies", href: "/movies", keywords: ["movie", "movies", "film", "cinema", "theater", "theatre", "showtime"] },
  { name: "Shopping", href: "/shopping", keywords: ["shop", "shopping", "store", "clothes", "clothing", "jewelry", "jewellery", "accessories", "accessory", "global brands", "saree", "sari"] },
  { name: "Grocery", href: "/grocery", keywords: ["grocery", "groceries", "supermarket", "produce", "spices"] },
  { name: "Party Venues", href: "/party-venues", keywords: ["venue", "venues", "banquet", "hall", "wedding hall", "event space"] },
  { name: "Event Management", href: "/event-management", keywords: ["decorator", "decorators", "decor", "floral", "decoration", "wedding decor", "event management", "event manager"] },
  { name: "Lawyers", href: "/lawyers", keywords: ["lawyer", "lawyers", "attorney", "legal", "immigration", "visa"] },
  { name: "Realtors", href: "/realtors", keywords: ["realtor", "realtors", "real estate", "property", "home", "house", "mortgage", "loan", "realty"] },
  { name: "Health & Wellness", href: "/health-wellness", keywords: ["doctor", "doctors", "clinic", "health", "wellness", "dentist", "physician", "medical", "hospital", "yoga", "ayurveda"] },
  { name: "Influencers", href: "/influencers", keywords: ["influencer", "influencers", "creator", "blogger", "social", "content"] },
  { name: "Models", href: "/models", keywords: ["model", "models", "modeling", "talent", "photoshoot", "campaign"] },
  { name: "Karaoke Spots", href: "/karaoke-spots", keywords: ["karaoke", "singing", "song", "music", "bar", "lounge", "night"] },
  { name: "Performing Artists", href: "/performing-artists", keywords: ["performing artist", "performing artists", "dance", "dancer", "dance academy", "dhol", "dholi", "singer", "singing"] },
  { name: "Cricket", href: "/cricket", keywords: ["cricket", "cricket league", "cricket club", "cricket team"] },
  { name: "Non Profit Org", href: "/non-profit-org", keywords: ["non profit", "nonprofit", "non profit org", "charity", "charities", "ngo", "donation", "donate", "hunger", "foundation"] },
  { name: "Photo/Video", href: "/photo-video", keywords: ["photo", "photos", "photography", "photographer", "video", "videos", "videography", "videographer", "studio", "shoot", "wedding photography"] },
  { name: "Financial Services", href: "/financial-services", keywords: ["financial", "finance", "financial services", "tax", "taxes", "accountant", "accounting", "insurance", "advisor", "retirement", "loan", "cpa"] },
  { name: "Dessert Shops", href: "/dessert-shops", keywords: ["dessert", "desserts", "sweet", "sweets", "dessert shops", "sweet temptations", "bakery", "bakers", "home bakers", "cake", "cakes", "ice cream", "pastries", "shakes", "gulab jamun"] },
  { name: "Marketing/production", href: "/event-production", keywords: ["marketing", "production", "marketing production", "event production", "event producer", "event organizer", "event organizers", "event management company"] },
  { name: "Salon/Spa", href: "/salon-spa", keywords: ["salon", "salons", "spa", "spas", "med spa", "medspa", "beauty salon", "hair salon", "nails", "facial", "grooming"] },
  { name: "Beauty Services", href: "/BeautyServices", keywords: ["beauty", "beauty services", "makeup", "makeup artist", "mua", "hair", "hairstyle", "hairstylist", "draping", "saree draping", "henna", "mehndi", "bridal makeup"] },
  { name: "DJ's", href: "/djs", keywords: ["dj", "djs", "dj's", "disc jockey", "wedding dj", "party dj"] },
  { name: "Media", href: "/media", keywords: ["media", "tv", "channel", "radio", "news", "publication", "broadcast", "radio jockey", "rj"] },
  { name: "Kids", href: "/kids", keywords: ["kid", "kids", "children", "child", "tutoring", "tutor", "learning center", "learning centers", "day care", "daycare", "montessori", "kids music"] },
  { name: "Catering", href: "/catering-only", keywords: ["catering", "caterer", "caterers", "catering services", "paan", "paancaters", "catering service"] },
  { name: "Senior Citizens", href: "/senior-citizen", keywords: ["senior", "seniors", "senior citizen", "senior citizens", "elderly", "retirement", "samaj", "nivas"] },
  { name: "Youth", href: "/youth", keywords: ["youth", "youth programs", "youth organizations", "life coach", "coaching", "young adults", "teen", "teens"] },
  { name: "Home Services", href: "/home-services", keywords: ["home service", "home services", "event assistance", "bartending", "bartender", "cleaning", "cleaner", "housekeeping"] },
];

// ── Keyword detection helpers ──
// Normalize: lowercase, strip punctuation (keep letters/numbers/spaces), and
// collapse repeated whitespace to single spaces, then trim. This makes the
// global search case-insensitive, punctuation-insensitive, and tolerant of
// extra spaces (e.g. "Doc.,  tor" and "doctor" both match "Doctor").
function normalize(s: string): string {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function matchCategory(q: string, keywords: string[]): boolean {
  const nq = normalize(q);
  if (!nq) return false;
  return keywords.some((k) => {
    const nk = normalize(k);
    return nq.includes(nk) || nk.includes(nq);
  });
}

// Generic partial-match helper for any field on a card/entry — normalizes both
// sides so "Dr. Lavanya!" matches a query of "lavanya" or "dr lavanya".
function fieldMatches(value: unknown, nq: string): boolean {
  if (!value) return false;
  if (Array.isArray(value)) return value.some((v) => fieldMatches(v, nq));
  return normalize(String(value)).includes(nq);
}

const RESTAURANT_KW = ALL_CATEGORIES.find((c) => c.name === "Restaurants")!.keywords;
const TEMPLE_KW = ALL_CATEGORIES.find((c) => c.name === "Temples/Spiritual Services")!.keywords;
const VENUE_KW = ALL_CATEGORIES.find((c) => c.name === "Party Venues")!.keywords;
const DECORATOR_KW = ALL_CATEGORIES.find((c) => c.name === "Event Management")!.keywords;
const MOVIE_KW = ALL_CATEGORIES.find((c) => c.name === "Movies")!.keywords;
const GROCERY_KW = ALL_CATEGORIES.find((c) => c.name === "Grocery")!.keywords;
const SHOPPING_KW = ALL_CATEGORIES.find((c) => c.name === "Shopping")!.keywords;
const EVENT_KW = [...ALL_CATEGORIES.find((c) => c.name === "Events")!.keywords, ...ALL_CATEGORIES.find((c) => c.name === "Concerts/Events")!.keywords];
const CRICKET_KW = ALL_CATEGORIES.find((c) => c.name === "Cricket")!.keywords;
const HEALTH_KW = ALL_CATEGORIES.find((c) => c.name === "Health & Wellness")!.keywords;
const ARTIST_KW = ALL_CATEGORIES.find((c) => c.name === "Performing Artists")!.keywords;
const NONPROFIT_KW = ALL_CATEGORIES.find((c) => c.name === "Non Profit Org")!.keywords;
const PHOTO_VIDEO_KW = ALL_CATEGORIES.find((c) => c.name === "Photo/Video")!.keywords;
const FINANCIAL_KW = ALL_CATEGORIES.find((c) => c.name === "Financial Services")!.keywords;
const DESSERT_KW = ALL_CATEGORIES.find((c) => c.name === "Dessert Shops")!.keywords;
const REALTOR_KW = ALL_CATEGORIES.find((c) => c.name === "Realtors")!.keywords;
const MARKETING_PRODUCTION_KW = ALL_CATEGORIES.find((c) => c.name === "Marketing/production")!.keywords;
const SALON_SPA_KW = ALL_CATEGORIES.find((c) => c.name === "Salon/Spa")!.keywords;
const BEAUTY_SERVICES_KW = ALL_CATEGORIES.find((c) => c.name === "Beauty Services")!.keywords;
const DJ_KW = ALL_CATEGORIES.find((c) => c.name === "DJ's")!.keywords;
const MEDIA_KW = ALL_CATEGORIES.find((c) => c.name === "Media")!.keywords;
const KIDS_KW = ALL_CATEGORIES.find((c) => c.name === "Kids")!.keywords;
const CATERING_KW = ALL_CATEGORIES.find((c) => c.name === "Catering")!.keywords;
const SENIOR_KW = ALL_CATEGORIES.find((c) => c.name === "Senior Citizens")!.keywords;
const YOUTH_KW = ALL_CATEGORIES.find((c) => c.name === "Youth")!.keywords;
const LAWYER_KW = ALL_CATEGORIES.find((c) => c.name === "Lawyers")!.keywords;
const HOME_SERVICES_KW = ALL_CATEGORIES.find((c) => c.name === "Home Services")!.keywords;

// ── Category pages with NO real card data (coming-soon pages) ──
const COMING_SOON = new Set(["Influencers", "Models"]);

export default function HomeSearchResults({
  query,
  location,
  onClear,
}: {
  query: string;
  location: string;
  onClear: () => void;
}) {
  // Normalized query used for ALL matching below — case-insensitive, ignores
  // punctuation, and collapses extra spaces (e.g. "doc" still matches
  // "Doctor"/"Doctors", "soul-food" matches "Soul Food").
  const q = normalize(query);
  const loc = location === "All Locations" ? undefined : location;
  // Uses the SAME merged source as the Restaurants page (Google Places +
  // admin-added custom_restaurants + food_trucks + hookah_lounges), so any
  // restaurant added via the admin form (e.g. "Soul Foods India") is
  // searchable immediately without a code change.
  const { items: places, loading } = useAllRestaurantPlaces(loc);

  // ── Detect which categories the query matches ──
  const isRestaurant = matchCategory(q, RESTAURANT_KW);
  const isTemple = matchCategory(q, TEMPLE_KW);
  const isVenue = matchCategory(q, VENUE_KW);
  const isDecorator = matchCategory(q, DECORATOR_KW);
  const isMovie = matchCategory(q, MOVIE_KW);
  const isGrocery = matchCategory(q, GROCERY_KW);
  const isShopping = matchCategory(q, SHOPPING_KW);
  const isEvent = matchCategory(q, EVENT_KW);
  const isCricket = matchCategory(q, CRICKET_KW);
  const isHealth = matchCategory(q, HEALTH_KW);
  const isArtist = matchCategory(q, ARTIST_KW);
  const isNonProfit = matchCategory(q, NONPROFIT_KW);
  const isPhotoVideo = matchCategory(q, PHOTO_VIDEO_KW);
  const isFinancial = matchCategory(q, FINANCIAL_KW);
  const isDessert = matchCategory(q, DESSERT_KW);
  const isRealtor = matchCategory(q, REALTOR_KW);
  const isMarketingProduction = matchCategory(q, MARKETING_PRODUCTION_KW);
  const isSalonSpa = matchCategory(q, SALON_SPA_KW);
  const isBeautyServices = matchCategory(q, BEAUTY_SERVICES_KW);
  const isDJ = matchCategory(q, DJ_KW);
  const isMedia = matchCategory(q, MEDIA_KW);
  const isKids = matchCategory(q, KIDS_KW);
  const isCatering = matchCategory(q, CATERING_KW);
  const isSenior = matchCategory(q, SENIOR_KW);
  const isYouth = matchCategory(q, YOUTH_KW);
  const isLawyer = matchCategory(q, LAWYER_KW);
  const isHomeService = matchCategory(q, HOME_SERVICES_KW);

  // ── Matched category pills ──
  const matchedCategories = q
    ? ALL_CATEGORIES.filter(
        (c) =>
          normalize(c.name).includes(q) ||
          c.keywords.some((k) => normalize(k).includes(q) || q.includes(normalize(k)))
      )
    : [];

  // ── Filtered data per category ──
  // Broad keywords (e.g. "food", "restaurant") make `isRestaurant` true, but we
  // still want a specific query like "Soul Foods" to rank its own name match
  // first instead of dumping the full unsorted restaurant list on top of it.
  const restaurantNameMatches = places.filter(
    (r) =>
      fieldMatches(r.name, q) ||
      fieldMatches(r.cuisines, q) ||
      fieldMatches(r.city, q)
  );
  const restaurantList = isRestaurant
    ? [
        ...restaurantNameMatches,
        ...places.filter((r) => !restaurantNameMatches.includes(r)),
      ]
    : restaurantNameMatches;

  const templeList = isTemple
    ? TEMPLES
    : TEMPLES.filter(
        (t) =>
          fieldMatches(t.name, q) ||
          fieldMatches(t.city, q) ||
          fieldMatches(t.address, q)
      );

  const venueList = isVenue
    ? VENUES
    : VENUES.filter(
        (v) =>
          fieldMatches(v.name, q) ||
          fieldMatches(v.city, q) ||
          fieldMatches(v.address, q)
      );

  const decoratorList = isDecorator
    ? DECORATORS
    : DECORATORS.filter(
        (d) =>
          fieldMatches(d.name, q) ||
          fieldMatches(d.city, q) ||
          (d.address !== "Address coming soon" && fieldMatches(d.address, q))
      );

  const movieList = isMovie
    ? MOVIES_DATA
    : MOVIES_DATA.filter(
        (m) =>
          fieldMatches(m.title, q) ||
          m.genres.some((g) => fieldMatches(g, q)) ||
          fieldMatches(m.theaterName, q) ||
          fieldMatches(m.city, q)
      );

  const groceryList = isGrocery
    ? GROCERY_STORES
    : GROCERY_STORES.filter(
        (g) =>
          fieldMatches(g.name, q) ||
          fieldMatches(g.location, q) ||
          fieldMatches(g.address, q)
      );

  // Sub-category match — "clothing", "jewelry", "accessories", etc. pull every
  // shopping card tagged with that category, not just literal name matches.
  const shoppingCategoryMatch = (s: { category?: string; categories?: string[] }) => {
    const cats = (s.categories || (s.category ? [s.category] : [])).map((c) => normalize(c));
    if (cats.length === 0) return false;
    const qNorm = q.replace(/s$/, ""); // "clothes"/"jewelries" -> loosen trailing plural
    return cats.some(
      (c) =>
        c.includes(q) ||
        q.includes(c) ||
        c.includes(qNorm) ||
        (q.includes("cloth") && c === "clothing") ||
        (q.includes("jewel") && (c === "jewelry" || c === "jewellery"))
    );
  };

  const shoppingList = isShopping
    ? SHOPPING_STORES
    : SHOPPING_STORES.filter(
        (s) =>
          fieldMatches(s.name, q) ||
          fieldMatches(s.location, q) ||
          fieldMatches(s.description, q) ||
          fieldMatches(s.address, q) ||
          shoppingCategoryMatch(s)
      );

  const cricketList = isCricket
    ? CRICKET_ENTRIES
    : CRICKET_ENTRIES.filter(
        (c: any) =>
          fieldMatches(c.name, q) ||
          fieldMatches(c.description, q) ||
          fieldMatches(c.city, q)
      );

  const healthList = isHealth
    ? HEALTH_PROVIDERS
    : HEALTH_PROVIDERS.filter(
        (h: any) =>
          fieldMatches(h.name, q) ||
          fieldMatches(h.clinic, q) ||
          fieldMatches(h.type, q) ||
          fieldMatches(h.specialty, q) ||
          fieldMatches(h.address, q) ||
          fieldMatches(h.location, q)
      );

  const eventList = isEvent
    ? EVENTS
    : EVENTS.filter(
        (e) =>
          fieldMatches(e.name, q) ||
          fieldMatches(e.city, q) ||
          fieldMatches(e.venue, q) ||
          fieldMatches(e.category, q)
      );

  const artistList = isArtist
    ? PERFORMING_ARTISTS_DATA
    : PERFORMING_ARTISTS_DATA.filter(
        (a: any) =>
          fieldMatches(a.name, q) ||
          fieldMatches(a.description, q)
      );

  const nonProfitList = isNonProfit
    ? NONPROFIT_ENTRIES
    : NONPROFIT_ENTRIES.filter(
        (n: any) =>
          fieldMatches(n.name, q) ||
          fieldMatches(n.description, q) ||
          fieldMatches(n.address, q)
      );

  const photoVideoList = isPhotoVideo
    ? PHOTO_VIDEO_ENTRIES
    : PHOTO_VIDEO_ENTRIES.filter((p: any) => fieldMatches(p.name, q));

  const dessertList = isDessert
    ? DESSERTS_DATA
    : DESSERTS_DATA.filter(
        (d: any) =>
          fieldMatches(d.name, q) ||
          fieldMatches(d.city, q) ||
          fieldMatches(d.address, q) ||
          (d.cuisines || []).some((c: string) => fieldMatches(c, q))
      );

  const financialList = isFinancial
    ? FINANCIAL_SERVICE_ENTRIES
    : FINANCIAL_SERVICE_ENTRIES.filter(
        (f: any) =>
          fieldMatches(f.name, q) ||
          fieldMatches(f.description, q) ||
          fieldMatches(f.address, q)
      );

  const realtorList = isRealtor
    ? REALTOR_ENTRIES
    : REALTOR_ENTRIES.filter(
        (r: any) =>
          fieldMatches(r.name, q) ||
          fieldMatches(r.description, q) ||
          fieldMatches(r.address, q)
      );

  const marketingProductionList = isMarketingProduction
    ? EVENT_ORGANIZERS
    : EVENT_ORGANIZERS.filter(
        (o: any) =>
          fieldMatches(o.name, q) ||
          fieldMatches(o.city, q) ||
          fieldMatches(o.address, q)
      );

  const salonSpaList = isSalonSpa
    ? SALON_SPA_STORES
    : SALON_SPA_STORES.filter(
        (s: any) =>
          fieldMatches(s.name, q) ||
          fieldMatches(s.description, q) ||
          fieldMatches(s.location, q) ||
          fieldMatches(s.address, q)
      );

  const beautyServicesList = isBeautyServices
    ? MAKEUP_ARTISTS
    : MAKEUP_ARTISTS.filter(
        (a: any) =>
          fieldMatches(a.name, q) ||
          fieldMatches(a.city, q) ||
          fieldMatches(a.type, q)
      );

  const djList = isDJ
    ? DJS
    : DJS.filter((d: any) => fieldMatches(d.name, q));

  const mediaList = isMedia
    ? MEDIA_ITEMS
    : MEDIA_ITEMS.filter(
        (m: any) =>
          fieldMatches(m.name, q) ||
          fieldMatches(m.description, q)
      );

  const kidsList = isKids
    ? KIDS_ENTRIES
    : KIDS_ENTRIES.filter(
        (k: any) =>
          fieldMatches(k.name, q) ||
          fieldMatches(k.type, q) ||
          fieldMatches(k.city, q) ||
          fieldMatches(k.address, q)
      );

  const cateringList = isCatering
    ? CATERERS
    : CATERERS.filter(
        (c: any) =>
          fieldMatches(c.name, q) ||
          fieldMatches(c.location, q) ||
          fieldMatches(c.address, q) ||
          fieldMatches(c.tagline, q) ||
          fieldMatches(c.type, q)
      );

  const seniorList = isSenior
    ? SENIOR_ENTRIES
    : SENIOR_ENTRIES.filter(
        (s: any) =>
          fieldMatches(s.name, q) ||
          fieldMatches(s.address, q)
      );

  const youthList = isYouth
    ? YOUTH_ENTRIES
    : YOUTH_ENTRIES.filter(
        (y: any) =>
          fieldMatches(y.name, q) ||
          fieldMatches(y.description, q) ||
          fieldMatches(y.address, q)
      );

  const lawyerList = isLawyer
    ? LAWYER_ENTRIES
    : LAWYER_ENTRIES.filter(
        (l: any) =>
          fieldMatches(l.name, q) ||
          fieldMatches(l.description, q) ||
          fieldMatches(l.address, q)
      );

  const homeServiceList = isHomeService
    ? HOME_SERVICES_ENTRIES
    : HOME_SERVICES_ENTRIES.filter(
        (h: any) =>
          fieldMatches(h.name, q) ||
          fieldMatches(h.description, q) ||
          fieldMatches(h.address, q) ||
          fieldMatches(h.phone, q)
      );

  // ── Concert-specific: filter Events to only Concerts ──
  const concertList = matchCategory(q, ALL_CATEGORIES.find((c) => c.name === "Concerts/Events")!.keywords)
    ? EVENTS.filter((e) => e.category === "Concert")
    : eventList;

  const showConcerts = matchCategory(q, ALL_CATEGORIES.find((c) => c.name === "Concerts/Events")!.keywords);

  const hasAnyResults =
    matchedCategories.length > 0 ||
    (isRestaurant && restaurantList.length > 0) ||
    restaurantList.length > 0 ||
    templeList.length > 0 ||
    venueList.length > 0 ||
    decoratorList.length > 0 ||
    movieList.length > 0 ||
    groceryList.length > 0 ||
    shoppingList.length > 0 ||
    eventList.length > 0 ||
    cricketList.length > 0 ||
    healthList.length > 0 ||
    artistList.length > 0 ||
    nonProfitList.length > 0 ||
    photoVideoList.length > 0 ||
    financialList.length > 0 ||
    dessertList.length > 0 ||
    realtorList.length > 0 ||
    marketingProductionList.length > 0 ||
    salonSpaList.length > 0 ||
    beautyServicesList.length > 0 ||
    djList.length > 0 ||
    mediaList.length > 0 ||
    kidsList.length > 0 ||
    cateringList.length > 0 ||
    seniorList.length > 0 ||
    youthList.length > 0 ||
    lawyerList.length > 0 ||
    homeServiceList.length > 0;

  // ── Derive a human-readable heading ──
  const primaryCategory = matchedCategories.length > 0 ? matchedCategories[0].name : null;

  return (
    <section className="bg-gray-50/50 min-h-[55vh] py-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-[18px] md:text-[20px] font-bold text-gray-900 truncate">
              Search results for "{query}"
            </h2>
            {location !== "All Locations" && (
              <p className="text-[13px] text-gray-500 mt-0.5 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-orange-500" /> {location}
              </p>
            )}
          </div>
          <button
            onClick={onClear}
            className="flex items-center gap-1.5 text-[13px] font-semibold text-orange-600 hover:text-orange-700 bg-white border border-orange-200 hover:bg-orange-50 rounded-full px-4 py-2 transition-colors shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </button>
        </div>

        {loading && isRestaurant ? (
          <div className="py-20 text-center text-gray-500 text-[14px]">Searching…</div>
        ) : hasAnyResults ? (
          <div className="space-y-10">
            {/* ── Category link pills (always show when matched) ── */}
            {matchedCategories.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">Categories</h3>
                <div className="flex flex-wrap gap-2.5">
                  {matchedCategories.map((c) => (
                    <Link
                      key={c.name}
                      to={c.href}
                      className="bg-white border border-gray-200 hover:border-orange-300 hover:shadow-sm rounded-full px-4 py-2 text-[13px] font-semibold text-gray-800 transition-all"
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* ── Restaurants (Google Places API + real RestaurantCard) ── */}
            {restaurantList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Restaurants <span className="text-gray-300">({restaurantList.length})</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {restaurantList.map((r, i) => (
                    <RestaurantCard key={r.id} restaurant={r} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Temples ── */}
            {templeList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Temples <span className="text-gray-300">({templeList.length})</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templeList.map((t, i) => (
                    <TempleCard key={t.name} temple={t} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Venues ── */}
            {venueList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Venues <span className="text-gray-300">({venueList.length})</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {venueList.map((v, i) => (
                    <VenueCard key={v.name} venue={v} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Event Management ── */}
            {decoratorList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Event Management <span className="text-gray-300">({decoratorList.length})</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {decoratorList.map((d, i) => (
                    <DecoratorCard key={d.name} decorator={d} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Movies ── */}
            {movieList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Movies <span className="text-gray-300">({movieList.length})</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {movieList.map((m, i) => (
                    <MovieCard key={m.id} movie={m} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Grocery ── */}
            {groceryList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Grocery Stores <span className="text-gray-300">({groceryList.length})</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groceryList.map((g, i) => (
                    <GroceryCard key={g.id} store={g} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Shopping ── */}
            {shoppingList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Shopping <span className="text-gray-300">({shoppingList.length})</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {shoppingList.map((s, i) => (
                    <ShoppingCard key={s.id} store={s} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Events / Concerts ── */}
            {showConcerts && concertList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Concerts <span className="text-gray-300">({concertList.length})</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {concertList.map((e, i) => (
                    <EventCard key={e.id} event={e} index={i} />
                  ))}
                </div>
              </div>
            )}

            {eventList.length > 0 && !showConcerts && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Events <span className="text-gray-300">({eventList.length})</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {eventList.map((e, i) => (
                    <EventCard key={e.id} event={e} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Cricket ── */}
            {cricketList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Cricket <span className="text-gray-300">({cricketList.length})</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cricketList.map((c: any, i: number) => (
                    <CricketCard key={c.name || i} entry={c} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Health & Wellness ── */}
            {healthList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Health & Wellness <span className="text-gray-300">({healthList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {healthList.map((h: any, i: number) => (
                    <HealthCard key={h.id || i} provider={h} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Performing Artists ── */}
            {artistList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Performing Artists <span className="text-gray-300">({artistList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {artistList.map((a: any, i: number) => (
                    <ArtistCard key={a.id || i} artist={a} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Non Profit Org ── */}
            {nonProfitList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Non Profit Org <span className="text-gray-300">({nonProfitList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {nonProfitList.map((n: any, i: number) => (
                    <NonProfitCard key={n.name || i} entry={n} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Photo/Video ── */}
            {photoVideoList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Photo/Video <span className="text-gray-300">({photoVideoList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photoVideoList.map((p: any, i: number) => (
                    <PhotoVideoCard key={p.id || i} entry={p} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Financial Services ── */}
            {financialList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Financial Services <span className="text-gray-300">({financialList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {financialList.map((f: any, i: number) => (
                    <FinancialServiceCard key={f.name || i} entry={f} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Dessert Shops ── */}
            {dessertList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Dessert Shops <span className="text-gray-300">({dessertList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {dessertList.map((d: any, i: number) => (
                    <DessertCard key={d.id || i} dessert={d} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Realtors ── */}
            {realtorList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Realtors <span className="text-gray-300">({realtorList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {realtorList.map((r: any, i: number) => (
                    <RealtorCard key={r.name || i} entry={r} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Lawyers ── */}
            {lawyerList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Lawyers <span className="text-gray-300">({lawyerList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {lawyerList.map((l: any, i: number) => (
                    <LawyerCard key={l.name || i} entry={l} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Home Services ── */}
            {homeServiceList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Home Services <span className="text-gray-300">({homeServiceList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {homeServiceList.map((h: any, i: number) => (
                    <HomeServiceCard key={h.name || i} entry={h} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Marketing/production ── */}
            {marketingProductionList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Marketing/production <span className="text-gray-300">({marketingProductionList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {marketingProductionList.map((o: any, i: number) => (
                    <OrganizerCard key={o.name || i} organizer={o} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Salon/Spa ── */}
            {salonSpaList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Salon/Spa <span className="text-gray-300">({salonSpaList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {salonSpaList.map((s: any, i: number) => (
                    <ShoppingCard key={s.id || i} store={s} index={i} compact />
                  ))}
                </div>
              </div>
            )}

            {/* ── Beauty Services ── */}
            {beautyServicesList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Beauty Services <span className="text-gray-300">({beautyServicesList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {beautyServicesList.map((a: any, i: number) => (
                    <MakeupArtistCard key={a.name || i} artist={a} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── DJ's ── */}
            {djList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  DJ's <span className="text-gray-300">({djList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {djList.map((d: any, i: number) => (
                    <DJCard key={d.name || i} dj={d} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Media ── */}
            {mediaList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Media <span className="text-gray-300">({mediaList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {mediaList.map((m: any, i: number) => (
                    <MediaCard key={m.id || i} item={m} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Senior Citizens ── */}
            {seniorList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Senior Citizens <span className="text-gray-300">({seniorList.length})</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {seniorList.map((s: any, i: number) => (
                    <SeniorCard key={s.name || i} entry={s} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Kids ── */}
            {kidsList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Kids <span className="text-gray-300">({kidsList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {kidsList.map((k: any, i: number) => (
                    <KidsCard key={k.name || i} entry={k} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Catering ── */}
            {cateringList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Catering <span className="text-gray-300">({cateringList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {cateringList.map((c: any, i: number) => (
                    <CatererCard key={c.name || i} caterer={c} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* ── Youth ── */}
            {youthList.length > 0 && (
              <div>
                <h3 className="text-[13px] font-bold text-gray-400 uppercase tracking-wide mb-3">
                  Youth <span className="text-gray-300">({youthList.length})</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                  {youthList.map((y: any, i: number) => (
                    <YouthCard key={y.name || i} entry={y} index={i} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="py-20 flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Search className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-[16px] font-bold text-gray-800">No results found for "{query}"</p>
            <p className="text-[13px] text-gray-500 mt-1 max-w-[420px]">
              We couldn't find anything matching your search
              {location !== "All Locations" ? ` in ${location}` : ""}. Try a different keyword or location.
            </p>
            <button
              onClick={onClear}
              className="mt-5 bg-[#f97316] hover:bg-[#ea580c] text-white text-[13px] font-semibold px-5 py-2.5 rounded-lg transition-colors"
            >
              Back to home
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
